import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import API from "../lib/api";
import { isTimeoutError, withTimeout } from "../lib/api";
import { Search, ArrowRight, Loader2, AlertTriangle, CheckCircle, Users, ShieldCheck, Activity, RadioTower } from "lucide-react";
import TeamRosterPanel from "../components/TeamRosterPanel";
import AgentInsightsFeed from "../components/AgentInsightsFeed";
import DebatePanel from "../components/DebatePanel";
import ConsolidatedFindingsBoard from "../components/ConsolidatedFindingsBoard";

const CONTROL_PREVIEW_LIMIT = 8;
const CONTROL_JOB_POLL_INTERVAL_MS = 1500;
const CONTROL_JOB_MAX_DURATION_MS = 45 * 60 * 1000;
const LAST_EVAL_STORAGE_PREFIX = "zentis:lastControlsEval:";



function renderEvaluationStartError(error) {
  if (isTimeoutError(error)) {
    return "Control evaluation timed out while starting.";
  }

  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.error || "Control evaluation failed to start.";
  }

  return error?.response?.data?.message || error?.message || "Control evaluation failed to start.";
}

function toSafeControlError(message) {
  const text = String(message || "").toLowerCase();
  if (text.includes("insufficient")) return "Insufficient evidence to validate this control.";
  if (text.includes("only one sql statement") || text.includes("query")) return "Control logic format was invalid; retry evaluation.";
  if (text.includes("timed out")) return "Evaluation timed out for this control.";
  return "Control evaluation failed.";
}

function describeNotExecutableControl(item) {
  const reason = String(item?.reason || item?.explanation || "").trim();
  const reasonCode = String(item?.reason_code || "").trim().toLowerCase();
  if (reason) return reason;
  if (reasonCode === "policy_only_control") return "Control is a broad regulatory or policy obligation rather than a row-level data test.";
  if (reasonCode === "control_logic_mismatch") return "Generated logic did not align with the control intent, so the control was treated as non-testable.";
  if (reasonCode === "sql_parse_failed") return "Logic generation failed because the returned checks could not be parsed.";
  if (reasonCode === "sql_unknown_table") return "Logic generation referenced tables outside the allowed artifact set.";
  if (reasonCode === "llm_not_executable") return "The logic generator determined that required fields were missing for this control.";
  return "The current artifact tables could not support a reliable data test for this control.";
}

function summarizeEvaluationOutcome(summary) {
  if (!summary || typeof summary !== "object") {
    return "Run evidence evaluation to analyze in-scope controls against CSV and OCR-extracted artifacts.";
  }
  const persisted = Number(summary.persisted_findings || 0);
  const evaluated = Number(summary.sql_candidate_controls || summary.evaluated_controls || 0);
  const insufficient = Number(summary.insufficient_data_controls || 0);
  const notTestable = Number(summary.not_testable_controls || 0);
  if (persisted > 0) {
    return `${persisted} finding(s) were created from ${evaluated || persisted} data-testable control(s).`;
  }
  if (insufficient > 0 || notTestable > 0) {
    return `Evaluation completed with no persisted findings. ${insufficient} control(s) lacked enough evidence and ${notTestable} control(s) were not data-testable.`;
  }
  return "Evaluation completed with no exceptions detected from the available artifact tables.";
}

function CoverageBadge({ coverage }) {
  const value = String(coverage || "").toLowerCase();
  if (!value) return null;
  const label = value === "partial" ? "Coverage: partial" : "Coverage: full";
  const cls = value === "partial"
    ? "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300"
    : "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300";
  return (
    <Badge variant="outline" className={`text-[10px] ${cls}`}>
      {label}
    </Badge>
  );
}

export default function Phase7EvidenceControls() {
  const navigate = useNavigate();
  const { auditRun } = useAudit();
  const [evaluating, setEvaluating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [findings, setFindings] = useState([]);
  const [controls, setControls] = useState([]);
  const [lastEvalSummary, setLastEvalSummary] = useState(null);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [lastTeamAnalysis, setLastTeamAnalysis] = useState(null);
  const [progressLabel, setProgressLabel] = useState("Preparing control evaluation...");

  const isTeamMode = auditRun?.audit_mode === "team";
  const inScopeControls = controls.filter((control) => control?.in_scope);
  const dataTestableControls = inScopeControls.filter((control) => control?.sql_testable !== false);
  const policyOnlyControls = inScopeControls.filter((control) => control?.sql_testable === false);

  const fetchFindings = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/findings`);
      setFindings(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch findings:", error);
    }
  }, [auditRun?.id]);

  const fetchControls = useCallback(async () => {
    if (!auditRun?.id || isTeamMode) return;
    setControlsLoading(true);
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/controls`);
      setControls(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch controls:", error);
      toast.error("Failed to load controls for evidence evaluation.");
    } finally {
      setControlsLoading(false);
    }
  }, [auditRun?.id, isTeamMode]);

  const fetchTeamData = useCallback(async () => {
    if (!auditRun?.id || !isTeamMode) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/team`);
      setTeamData(data);
    } catch (error) {
      console.error("Failed to fetch team data:", error);
    }
  }, [auditRun?.id, isTeamMode]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  useEffect(() => {
    fetchControls();
    fetchFindings();
  }, [fetchControls, fetchFindings]);

  // Restore last evaluation summary (so insufficient evidence doesn't "vanish" after refresh/navigation)
  useEffect(() => {
    if (!auditRun?.id) return;
    try {
      const raw = localStorage.getItem(`${LAST_EVAL_STORAGE_PREFIX}${auditRun.id}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        setLastEvalSummary((current) => current || parsed);
      }
    } catch (e) {
      // ignore corrupted storage
    }
  }, [auditRun?.id]);

  const handleEvaluate = async () => {
    if (!auditRun?.id) {
      toast.error("Audit run is not available.");
      return;
    }
    if (inScopeControls.length === 0) {
      toast.error("No in-scope controls found. Complete Phase 3 and Phase 4 before running evaluation.");
      return;
    }
    if (dataTestableControls.length === 0) {
      toast.error("No data-testable controls found. Refine Phase 3 controls into data-verifiable checks before running evaluation.");
      return;
    }

    try {
      setEvaluating(true);
      setProgress(5);
      setProgressLabel("Preparing control evaluation...");

      const { data } = await API.post(
        `/audit-runs/${auditRun.id}/evaluate/sql-controls`,
        {
          artifact_ids: [],
          explain_result: true,
        },
        withTimeout()
      );
      setLastEvalSummary(null);

      if (!data?.job_id) {
        setEvaluating(false);
        setProgress(0);
        setProgressLabel("Evaluation failed to start.");
        toast.error(data?.message || "Failed to start  control evaluation.");
        return;
      }

      setJobId(data.job_id);
      const pollStartedAt = Date.now();

      const pollJob = async () => {
        try {
          const { data: status } = await API.get(`/job-status/${data.job_id}`, withTimeout());

          if (status.state === "PENDING") {
            setProgress((current) => Math.max(current, 10));
            setProgressLabel("Queued for control evaluation...");
          } else if (status.state === "PROGRESS") {
            const step = status.meta?.step;

            if (step === "load_controls") {
              setProgress((current) => Math.max(current, 20));
              setProgressLabel("Loading Phase 3 controls and CSV/OCR artifact tables...");
            } else if (step === "evaluate_controls") {
              const currentIndex = Number(status.meta?.current_index || 0);
              const totalControls = Number(status.meta?.sql_candidate_controls || status.meta?.total_controls || dataTestableControls.length || 1);
              const nextProgress = Math.max(25, Math.min(90, Math.round(25 + (currentIndex / totalControls) * 55)));
              setProgress((current) => Math.max(current, nextProgress));
              setProgressLabel(
                status.meta?.current_control
                  ? `Evaluating ${status.meta.current_control} against CSV/OCR artifacts...`
                  : "Evaluating controls against CSV/OCR artifacts..."
              );
            } else if (step === "finalize") {
              setProgress((current) => Math.max(current, 92));
              setProgressLabel("Saving evaluation findings...");
            }
          } else if (status.state === "SUCCESS") {
            setProgress(100);
            setProgressLabel("Evaluation complete.");
            setEvaluating(false);
            setJobId(null);

            const result = status.result || {};
            setLastEvalSummary(result);
            try {
              localStorage.setItem(
                `${LAST_EVAL_STORAGE_PREFIX}${auditRun.id}`,
                JSON.stringify({ ...result, saved_at: new Date().toISOString() })
              );
            } catch (e) {
              // storage might be full / disabled; ignore
            }
            await fetchFindings();
            if (result?.persisted_findings) {
              toast.success(
                `Evaluation complete. ${result.persisted_findings} findings persisted from ${result.sql_candidate_controls || result.evaluated_controls || 0} data-testable controls.`
              );
            } else if (Array.isArray(result?.results) && result.results.some((entry) => entry?.status === "NOT_EXECUTABLE")) {
              const nonExecutableCount = Number(result?.not_testable_controls || result.results.filter((entry) => entry?.status === "NOT_EXECUTABLE").length);
              const insufficientCount = Number(result?.insufficient_data_controls || result.results.filter((entry) => entry?.status === "INSUFFICIENT_DATA").length);
              toast.warning(
                `No findings were created. ${nonExecutableCount} control(s) were policy-only or not data-testable${insufficientCount ? ` and ${insufficientCount} lacked enough evidence` : ""}.`
              );
            } else if (Array.isArray(result?.results) && result.results.some((entry) => entry?.status === "INSUFFICIENT_DATA")) {
              const count = result.results.filter((entry) => entry?.status === "INSUFFICIENT_DATA").length;
              toast.warning(
                `Evaluation completed. ${count} control(s) were skipped due to insufficient evidence.`
              );
            } else if (Array.isArray(result?.failed_controls) && result.failed_controls.length > 0) {
              toast.warning(
                `Evaluation completed with 0 findings. ${result.failed_controls.length} control(s) failed validation.`
              );
            } else {
              toast.success("Evaluation complete.");
            }
            return;
          } else if (status.state === "FAILURE" || status.state === "REVOKED") {
            setEvaluating(false);
            setJobId(null);
            setProgress(0);
            setProgressLabel("Evaluation failed.");
            toast.error(status.error || "Control evaluation job failed.");
            return;
          }

          if (Date.now() - pollStartedAt >= CONTROL_JOB_MAX_DURATION_MS) {
            setEvaluating(false);
            setJobId(null);
            setProgress(0);
            setProgressLabel("Evaluation timed out.");
            toast.error("Control evaluation timed out. Please try again.");
            return;
          }

          setTimeout(pollJob, CONTROL_JOB_POLL_INTERVAL_MS);
        } catch (error) {
          console.error("Failed to poll control evaluation status:", error);
          setEvaluating(false);
          setJobId(null);
          setProgress(0);
          setProgressLabel("Failed to check evaluation status.");
          toast.error("Failed to check  control evaluation status.");
        }
      };

      setTimeout(pollJob, CONTROL_JOB_POLL_INTERVAL_MS);
    } catch (error) {
      console.error("Failed to start control evaluation:", error);
      setEvaluating(false);
      setJobId(null);
      setProgress(0);
      setProgressLabel("Evaluation failed to start.");
      toast.error(renderEvaluationStartError(error));
    }
  };

  const handleTeamAnalysis = async (result = null) => {
    if (result) setLastTeamAnalysis(result);
    await fetchTeamData();
    await fetchFindings();
  };

  const handleContinue = async () => {
    navigate("/phase/7-preview");
  };

  const severityColor = (severity) => ({
    High: "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800",
    Medium: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800",
    Low: "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800",
    Observation: "text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-950/30 border-slate-200 dark:border-slate-800",
  }[severity] || "");
  const insufficientControls = Array.isArray(lastEvalSummary?.results)
    ? lastEvalSummary.results.filter((result) => result?.status === "INSUFFICIENT_DATA")
    : [];
  const notExecutableControls = Array.isArray(lastEvalSummary?.results)
    ? lastEvalSummary.results.filter((result) => result?.status === "NOT_EXECUTABLE")
    : [];
  const insufficientControlCount = insufficientControls.length;
  const visibleInsufficientControls = insufficientControls.slice(0, 5);
  const notExecutableControlCount = notExecutableControls.length;
  const visibleNotExecutableControls = notExecutableControls.slice(0, 5);
  const controlReadiness = inScopeControls.length
    ? Math.round((dataTestableControls.length / inScopeControls.length) * 100)
    : 0;
  const teamAgents = Array.isArray(teamData?.team_config?.agents) ? teamData.team_config.agents : [];
  const teamObservations = Array.isArray(teamData?.agent_observations) ? teamData.agent_observations : [];
  const consolidatedTeamFindings = Array.isArray(teamData?.consolidated_findings) ? teamData.consolidated_findings : [];
  const validatedTeamFindings = consolidatedTeamFindings.filter((finding) => finding?.status === "validated").length;
  const disputedTeamFindings = consolidatedTeamFindings.filter((finding) => finding?.disputed).length;
  const phase7Stats = isTeamMode
    ? [
        {
          label: "Audit Team",
          value: teamAgents.length,
          sub: teamAgents.length ? "Active agents" : "No agents configured",
        },
        {
          label: "Agent Insights",
          value: teamObservations.length,
          sub: teamObservations.length ? "Observations raised" : "No insights yet",
        },
        {
          label: "Team Findings",
          value: consolidatedTeamFindings.length,
          sub: consolidatedTeamFindings.length
            ? `${validatedTeamFindings} validated${disputedTeamFindings ? ` • ${disputedTeamFindings} disputed` : ""}`
            : "None yet",
        },
      ]
    : [
        {
          label: "In Scope",
          value: controlsLoading ? "..." : inScopeControls.length,
          sub: "Phase 3 controls",
        },
        {
          label: "Data Testable",
          value: controlsLoading ? "..." : dataTestableControls.length,
          sub: `${controlReadiness}% readiness`,
        },
        {
          label: "Findings",
          value: findings.length,
          sub: findings.length ? "Persisted" : "None yet",
        },
      ];


  return (
    <div className="phase7-console space-y-6" data-testid="phase-7">
      <div className="phase7-hero overflow-hidden rounded-2xl border p-5 text-white shadow-2xl">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge className="border-white/15 bg-white/10 text-cyan-100 hover:bg-white/10">
                <RadioTower className="mr-1 h-3 w-3" /> System Protocol Active
              </Badge>
              {isTeamMode && (
                <Badge className="border-violet-300/30 bg-violet-400/15 text-violet-100 hover:bg-violet-400/15">
                  <Users className="mr-1 h-3 w-3" /> Team Mode Active
                </Badge>
              )}
            </div>
            <h2 className="max-w-xl text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">
              Evidence evaluation command center
            </h2>
          </div>
          <div className="flex flex-wrap justify-start gap-2 lg:justify-end">
            {!isTeamMode && (
              <Button
                onClick={handleEvaluate}
                disabled={evaluating || !auditRun || inScopeControls.length === 0 || dataTestableControls.length === 0}
                data-testid="evaluate-evidence-btn"
                className="bg-violet-500 text-white shadow-lg shadow-violet-950/30 hover:bg-violet-400"
              >
                {evaluating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                {evaluating ? "Evaluating..." : "Run Evaluation"}
              </Button>
            )}
            <Button
              onClick={handleContinue}
              disabled={!isTeamMode && findings.length === 0}
              data-testid="continue-to-findings-top-btn"
              className="border border-white/15 bg-white/10 text-white shadow-lg hover:bg-white/15"
            >
              <ArrowRight className="mr-2 h-4 w-4" /> Continue to Evidence Preview
            </Button>
          </div>
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          {phase7Stats.map((stat, index) => (
            <div
              key={stat.label}
              className="phase7-stat metric-card-anim rounded-xl border border-white/10 bg-white/[0.06] p-4"
              style={{ "--delay": `${120 + index * 90}ms` }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-slate-400">{stat.label}</span>
                <Activity className="h-3.5 w-3.5 text-cyan-300" />
              </div>
              <p className="mt-2 font-mono text-3xl font-bold text-white">{stat.value}</p>
              <p className="mt-1 text-xs text-slate-400">{stat.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {isTeamMode && teamData && (
        <div className="space-y-6">
          {teamData.team_executive_summary && (
            <Card className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/30">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm">Team Analysis Summary</h3>
                    <p className="text-sm text-muted-foreground mt-1">{teamData.team_executive_summary}</p>
                    {teamData.team_next_steps?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium mb-1">Recommended Next Steps:</p>
                        <ul className="space-y-1">
                          {teamData.team_next_steps.slice(0, 3).map((step, index) => (
                            <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                              <span className="text-emerald-500">-&gt;</span> {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-4">
              <TeamRosterPanel
                teamConfig={teamData.team_config}
                auditRunId={auditRun.id}
                onUpdate={fetchTeamData}
                onRunAnalysis={handleTeamAnalysis}
                analysisEndpoint={`/audit-runs/${auditRun.id}/team/run-sql-analysis?phase=full`}
                runButtonLabel="Run Team Analysis"
              />
              <DebatePanel debates={teamData.debate_threads || []} auditRunId={auditRun.id} onUpdate={fetchTeamData} />
            </div>

            <div className="lg:col-span-2 space-y-4">
              {Array.isArray(lastTeamAnalysis?.failed_controls) && lastTeamAnalysis.failed_controls.length > 0 && (
                <Card className="border-amber-300/50 bg-amber-50/50 dark:border-amber-700/40 dark:bg-amber-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Rejected SQL Controls</CardTitle>
                    <p className="text-xs text-muted-foreground">
                      These proposals did not compile or execute cleanly against the current artifact schema.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {lastTeamAnalysis.failed_controls.slice(0, 8).map((item, index) => (
                      <div
                        key={`${item.agent || "agent"}-${item.control_id || index}`}
                        className="rounded-lg border border-amber-300/40 bg-background/40 px-3 py-2 text-xs"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{item.agent || "Agent"}</Badge>
                          {item.control_id && (
                            <span className="font-mono text-muted-foreground">{item.control_id}</span>
                          )}
                        </div>
                        <p className="mt-1 text-foreground/90">{toSafeControlError(item.message)}</p>
                        {String(item.message || "").trim() && (
                          <p className="mt-1 text-[11px] text-muted-foreground">{String(item.message)}</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              <AgentInsightsFeed
                observations={teamData.agent_observations || []}
                onValidate={() => toast.info("Validation coming soon")}
                onDispute={() => toast.info("Dispute feature coming soon")}
              />

              {teamData.consolidated_findings?.length > 0 && (
                <ConsolidatedFindingsBoard
                  findings={teamData.consolidated_findings}
                  onViewDetails={(finding) => toast.info(`View details for: ${finding.finding_title}`)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {!isTeamMode && (
        <>
          <Card className="phase7-console-card border-blue-200 dark:border-blue-800/40">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-500" />
                Control Evidence Audit
              </CardTitle>
              {inScopeControls.length > CONTROL_PREVIEW_LIMIT && (
                      <p className="text-xs text-muted-foreground">
                        Showing {CONTROL_PREVIEW_LIMIT} of {inScopeControls.length} in-scope controls. Run evaluation to process the full set.
                      </p>
                    )}
              {/* <p className="text-xs text-muted-foreground">
                Run all in-scope Phase 3 controls through automated logic generation and evaluate them against uploaded sampling artifacts.
              </p> */}
            </CardHeader>
            <CardContent className="space-y-4">


              <div className="phase7-control-panel rounded-lg border border-border/70 bg-muted/20 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Controls selected from Phase 3</p>

                  </div>
                  <Button variant="outline" size="sm" onClick={fetchControls} disabled={controlsLoading || evaluating}>
                    {controlsLoading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : null}
                    Refresh Controls
                  </Button>
                </div>

                {controlsLoading ? (
                  <div className="text-xs text-muted-foreground">Loading control inventory...</div>
                ) : inScopeControls.length > 0 ? (
                  <div className="space-y-2">
                    {inScopeControls.slice(0, CONTROL_PREVIEW_LIMIT).map((control) => (
                      <div key={control.id || control.control_id} className="phase7-control-row rounded-md border border-border/60 bg-background/70 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono font-semibold text-blue-600 dark:text-blue-400">
                            {control.control_id || "CONTROL"}
                          </span>
                          <Badge variant="outline" className="text-[10px]">In Scope</Badge>

                        </div>
                        <p className="mt-1 text-sm">{control.description || "No control description available."}</p>
                        {control?.sql_testable === false && control?.sql_testability_reason && (
                          <p className="mt-1 text-xs text-muted-foreground">{control.sql_testability_reason}</p>
                        )}
                      </div>
                    ))}
                    
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800/40 dark:bg-amber-950/20 dark:text-amber-300">
                    No in-scope controls found. Mark Phase 3 controls as in scope before running evaluation.
                  </div>
                )}
              </div>

              {evaluating && (
                <div className="phase7-progress-card rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50/40 dark:bg-blue-950/20 p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span>{jobId ? progressLabel : "Preparing control evaluation..."}</span>
                    <span className="font-mono text-xs">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    Evaluating only the data-testable Phase 3 controls against sampling artifacts and skipping policy-only controls automatically.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {findings.length > 0 && (
            <div className="space-y-3">
              {insufficientControls.length > 0 && (
                <Card className="border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20">
                  <CardContent className="py-4">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2">
                      Insufficient Evidence Controls ({insufficientControls.length})
                    </p>
                    <div className="space-y-1.5">
                      {insufficientControls.slice(0, 8).map((item, idx) => (
                        <div key={`${item.control_id || "CONTROL"}-${idx}`} className="text-xs text-amber-800 dark:text-amber-200">
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{item.control_id || "CONTROL"}</span>
                            <CoverageBadge coverage={item.coverage} />
                            <span>{" - "}Skipped due to insufficient evidence for reliable validation.</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {notExecutableControls.length > 0 && (
                <Card className="border-slate-300/60 bg-slate-50/70 dark:bg-slate-950/20">
                  <CardContent className="py-4">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      Non-Executable Controls ({notExecutableControls.length})
                    </p>
                    <div className="space-y-1.5">
                      {notExecutableControls.slice(0, 8).map((item, idx) => (
                        <div key={`${item.control_id || "CONTROL"}-notexec-${idx}`} className="text-xs text-slate-800 dark:text-slate-200">
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{item.control_id || "CONTROL"}</span>
                            <span>{" - "}{describeNotExecutableControl(item)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              <div className="flex gap-3">
                {["High", "Medium", "Low", "Observation"].map((severity) => {
                  const count = findings.filter((finding) => finding.suggested_severity === severity).length;
                  return count > 0 ? (
                    <div key={severity} className={`px-3 py-2 rounded-md border ${severityColor(severity)}`}>
                      <p className="text-xl font-bold font-mono">{count}</p>
                      <p className="text-[10px] uppercase tracking-wider font-semibold">{severity}</p>
                    </div>
                  ) : null;
                })}
              </div>

              {findings.map((finding) => (
                <Card key={finding.id} data-testid={`finding-card-${finding.control_id}`}>
                  <CardContent className="py-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {finding.suggested_severity === "High" ? (
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        )}
                        <span className="font-mono text-sm font-semibold">{finding.control_id}</span>
                        <CoverageBadge coverage={finding.coverage || finding?.sql_audit?.coverage} />
                      </div>
                      <Badge variant="outline" className={`text-xs border ${severityColor(finding.suggested_severity)}`}>
                        {finding.suggested_severity}
                      </Badge>
                    </div>
                    <p className="text-sm">{finding.finding_description}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="p-2 bg-muted rounded">
                        <p className="text-muted-foreground">Evidence Score</p>
                        <p className="font-mono font-semibold">{(finding.evidence_sufficiency_score * 100).toFixed(0)}%</p>
                      </div>
                      <div className="p-2 bg-muted rounded">
                        <p className="text-muted-foreground">Reg. Exposure</p>
                        <p className="font-mono font-semibold">{(finding.regulatory_exposure_score * 100).toFixed(0)}%</p>
                      </div>
                      <div className="p-2 bg-muted rounded">
                        <p className="text-muted-foreground">Confidence</p>
                        <p className="font-mono font-semibold">{(finding.confidence_score * 100).toFixed(0)}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!evaluating && findings.length === 0 && (
            <Card className="phase7-console-card">
              <CardContent className="min-h-[300px] flex flex-col items-center justify-center text-center px-6">
                <Search className="w-9 h-9 text-blue-500/70 mb-3" />
                <p className="text-sm font-medium">No findings generated yet.</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  {lastEvalSummary ? summarizeEvaluationOutcome(lastEvalSummary) : "Run evidence evaluation to analyze in-scope controls against CSV and OCR-extracted artifacts. Findings severity and confidence will appear here."}
                </p>
                {Array.isArray(lastEvalSummary?.failed_controls) && lastEvalSummary.failed_controls.length > 0 && (
                  <div className="mt-4 w-full max-w-2xl rounded-md border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 p-3 text-left">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2">
                      Last evaluation completed with control errors
                    </p>
                    <div className="space-y-1.5">
                      {lastEvalSummary.failed_controls.slice(0, 5).map((fc, idx) => (
                        <div key={`${fc.control_id || "CONTROL"}-${idx}`} className="text-xs text-amber-800 dark:text-amber-200">
                          <span className="font-mono">{fc.control_id || "CONTROL"}</span>
                          {" - "}
                          {toSafeControlError(fc.message)}
                        </div>
                      ))}
                      {lastEvalSummary.failed_controls.length > 5 && (
                        <p className="text-[11px] text-amber-700/90 dark:text-amber-300/90">
                          +{lastEvalSummary.failed_controls.length - 5} more failed control(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {insufficientControlCount > 0 && (
                  <div className="mt-4 w-full max-w-2xl rounded-md border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 p-3 text-left">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2">
                      Controls skipped due to insufficient evidence
                    </p>
                    <div className="space-y-1.5">
                      {visibleInsufficientControls.map((item, idx) => (
                        <div key={`${item.control_id || "CONTROL"}-ins-${idx}`} className="text-xs text-amber-800 dark:text-amber-200">
                          <span className="font-mono">{item.control_id || "CONTROL"}</span>
                          {" - "}Missing required evidence signals.
                        </div>
                      ))}
                      {insufficientControlCount > visibleInsufficientControls.length && (
                        <p className="text-[11px] text-amber-700/90 dark:text-amber-300/90">
                          +{insufficientControlCount - visibleInsufficientControls.length} more skipped control(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {notExecutableControlCount > 0 && (
                  <div className="mt-4 w-full max-w-2xl rounded-md border border-slate-300/60 bg-slate-50/70 dark:bg-slate-950/20 p-3 text-left">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      Controls skipped during evidence evaluation
                    </p>
                    <div className="space-y-1.5">
                      {visibleNotExecutableControls.map((item, idx) => (
                        <div key={`${item.control_id || "CONTROL"}-notexec-empty-${idx}`} className="text-xs text-slate-800 dark:text-slate-200">
                          <span className="font-mono">{item.control_id || "CONTROL"}</span>
                          {" - "}{describeNotExecutableControl(item)}
                        </div>
                      ))}
                      {notExecutableControlCount > visibleNotExecutableControls.length && (
                        <p className="text-[11px] text-slate-700/90 dark:text-slate-300/90">
                          +{notExecutableControlCount - visibleNotExecutableControls.length} more skipped control(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {notExecutableControls.length > 0 && (
                  <div className="mt-4 w-full max-w-2xl rounded-md border border-slate-300/60 bg-slate-50/70 dark:bg-slate-950/20 p-3 text-left">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      Controls that did not produce an executable data test
                    </p>
                    <div className="space-y-1.5">
                      {notExecutableControls.slice(0, 5).map((item, idx) => (
                        <div key={`${item.control_id || "CONTROL"}-notexec-empty-${idx}`} className="text-xs text-slate-800 dark:text-slate-200">
                          <span className="font-mono">{item.control_id || "CONTROL"}</span>
                          {" - "}{describeNotExecutableControl(item)}
                        </div>
                      ))}
                      {notExecutableControls.length > 5 && (
                        <p className="text-[11px] text-slate-700/90 dark:text-slate-300/90">
                          +{notExecutableControls.length - 5} more non-executable control(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={!isTeamMode && findings.length === 0} data-testid="continue-to-findings-btn">
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Evidence Preview
        </Button>
      </div>
    </div>
  );
}
